import { useDroppable } from "@dnd-kit/core";
import React, { useRef, useState } from "react";
import {
  getTerrainVisualizationOptions,
  subscribeTerrainVisualizationOptions,
} from "../utils/terrainVisualizationOptions";
import { useAutoFitCameraOnMeshLoad } from "./hooks/useAutoFitCameraOnMeshLoad";
import { useActuatorMarkers } from "./hooks/useActuatorMarkers";
import { useCollisionGeomAppearance } from "./hooks/useCollisionGeomAppearance";
import { useDebugPivotSpheres } from "./hooks/useDebugPivotSpheres";
import { useFocusModeDim } from "./hooks/useFocusModeDim";
import { useGizmoControllers } from "./hooks/useGizmoControllers";
import { useGizmoTargetResolvers } from "./hooks/useGizmoTargetResolvers";
import { useGraspOverlays } from "./hooks/useGraspOverlays";
import { useIkTargetFollower } from "./hooks/useIkTargetFollower";
import { useInventoryLabels } from "./hooks/useInventoryLabels";
import { useJointKinematicsEvents } from "./hooks/useJointKinematicsEvents";
import { useJointMarkerSizingState } from "./hooks/useJointMarkerSizingState";
import { useSceneJointRuntime } from "./hooks/useSceneJointRuntime";
import { useJointVisualizationEvents } from "./hooks/useJointVisualizationEvents";
import { useLibraryDropPlacement } from "./hooks/useLibraryDropPlacement";
import { useMeshAppearance } from "./hooks/useMeshAppearance";
import { useMeshEditorPreviewTransforms } from "./hooks/useMeshEditorPreviewTransforms";
import { useMassPropertiesComMarkers } from "./hooks/useMassPropertiesComMarkers";
import { useMeasurementOverlays } from "./hooks/useMeasurementOverlays";
import { usePendingBodyMeshLoads } from "./hooks/usePendingBodyMeshLoads";
import { useSavedCameraMarkers } from "./hooks/useSavedCameraMarkers";
import { useSavedLightMarkers } from "./hooks/useSavedLightMarkers";
import { useSceneLightObjects } from "./hooks/useSceneLightObjects";
import { useSceneBodyTransformSync } from "./hooks/useSceneBodyTransformSync";
import { useSceneConstraintVisualizationController } from "./hooks/useSceneConstraintVisualizationController";
import { useSceneDisposeOnUnmount } from "./hooks/useSceneDisposeOnUnmount";
import { useSceneFloorSubscriptions } from "./hooks/useSceneFloorSubscriptions";
import { useSceneGraphCycleRepair } from "./hooks/useSceneGraphCycleRepair";
import { useSceneInteractionController } from "./hooks/useSceneInteractionController";
import { useScenePhysicsLifecycle } from "./hooks/useScenePhysicsLifecycle";
import { useSceneRebuild } from "./hooks/useSceneRebuild";
import { useSceneRuntimeRefs } from "./hooks/useSceneRuntimeRefs";
import { useSceneSelectionOverlays } from "./hooks/useSceneSelectionOverlays";
import { useSceneStructureSnapshot } from "./hooks/useSceneStructureSnapshot";
import { useSceneVisibilityRules } from "./hooks/useSceneVisibilityRules";
import { useSceneVisibilityUiSubscriptions } from "./hooks/useSceneVisibilityUiSubscriptions";
import { useSceneViewportState } from "./hooks/useSceneViewportState";
import { useSelectionHighlightOverlays } from "./hooks/useSelectionHighlightOverlays";
import { useSelectedLayerVisibilityNotice } from "./hooks/useSelectedLayerVisibilityNotice";
import { useSiteVizAppearance } from "./hooks/useSiteVizAppearance";
import { useThreeJsRenderer } from "./hooks/useThreeJsRenderer";
import { useViewportBodyVisibilitySync } from "./hooks/useViewportBodyVisibilitySync";
import { useViewportLightingSync } from "./hooks/useViewportLightingSync";
import { SceneViewportLayout } from "./SceneViewportLayout";
// import removed: camera should not auto-move based on selection
import { SCENE_LIBRARY_DROPZONE_ID } from "../constants/libraryDragDndIds";
import { useFileStore } from "../stores/fileStore";
import { useMetricsActions } from "../stores/metricsStore";
import { useBodies, useSceneActions, useSceneStore, useWorldBodies } from "../stores/sceneStore";
import { useHistorySync } from "./hooks/useHistorySync";
import { useMaterialSync } from "./hooks/useMaterialSync";
import { usePhysicsPoses } from "./hooks/usePhysicsPoses";

import { EMPTY_OBJECT } from "./scene/sceneConstants";

function Scene() {
  const fileMap = useFileStore((state) => state.fileMap);
  const {
    autoFitCameraRef,
    bodyTreeStructureSignatureRef,
    cameraRef,
    constraintHoveredKeyRef,
    constraintMarkerBuildRetryCountRef,
    constraintMarkerMeshesRef,
    constraintMarkersGroupRef,
    constraintPointerRef,
    constraintVisualizationRef,
    controlsRef,
    externalForceVizRef,
    fileMapSignatureRef,
    ikMarkerFocusGraspRef,
    isHistoryRestoringRef,
    isOrbitingRef,
    jointLabelRefs,
    jointObjectsRef,
    labelRendererRef,
    missingConstraintBodyToastKeysRef,
    modelGroupRef,
    mountRef,
    padPoseLogDoneRef,
    pendingMeshLoadBodyCountsRef,
    physicsPoseApplyLoggedRef,
    physicsPoseLogDoneRef,
    positionGizmoControllerRef,
    preferPointerAfterOrbitRef,
    previousBodiesRef,
    previousFileMapKeysRef,
    removedBodyCacheRef,
    rendererRef,
    rolloutProbeRef,
    rotationGizmoControllerRef,
    scaleGizmoControllerRef,
    sceneBuildTokenRef,
    sceneObjectsRef,
    sceneRef,
    selectedGraspByBodyRef,
    siteLabelsRef,
    slideJointLimitControlsRef,
    tendonVisualizationRef,
    zeroBodySyncWarningSignatureRef,
  } = useSceneRuntimeRefs();
  const { setNodeRef: setSceneDropNodeRef } = useDroppable({
    id: SCENE_LIBRARY_DROPZONE_ID,
  });
  const setMountNodeRef = React.useCallback(
    (node) => {
      mountRef.current = node;
      setSceneDropNodeRef(node);
    },
    [setSceneDropNodeRef, mountRef]
  );
  const [sceneObjects, setSceneObjects] = useState({});
  const {
    pendingMeshLoadBodyVersion,
    registerPendingBodyMeshLoad,
    updatePendingMeshLoadBodyCount,
  } = usePendingBodyMeshLoads({ pendingMeshLoadBodyCountsRef });
  const [structureChangeTick, setStructureChangeTick] = useState(0);
  const {
    applyJointMarkerSizes,
    initialBuildDoneRef,
    jointMarkerAutoResizeRebuildGuardRef,
    jointMarkerSizes,
    lastHandledStructureChangeTickRef,
    setJointMarkerSizes,
  } = useJointMarkerSizingState({ setStructureChangeTick });

  const {
    bodyVisibilityUi,
    constraintVisibilityUi,
    inertialVisibilityUi,
    inventoryLabelVisibility,
    jointMarkerScalePercent,
    jointVisibilityUi,
    selectionHighlightUi,
    showJointPositionSlider,
    showRotationGizmo,
    showSavedCameraMarkers,
    showScaleGizmo,
    showViewportBodies,
    siteVisibilityUi,
  } = useSceneVisibilityUiSubscriptions({
    sceneRef,
    applyJointMarkerSizes,
    setStructureChangeTick,
  });

  // Get metrics store actions
  const metricsActions = useMetricsActions();
  const lastMetricsUpdate = useRef(0);

  const {
    actuatorDisplayLabelByJointUuid,
    actuatorSignature,
    actuatorSliderState,
    actuators,
    actuatedJointUuids,
    allEqualities,
    allMeshes,
    allSites,
    allTendons,
    bodyVisibility,
    cameraMarkerVisibility,
    collisionGeomColor,
    collisionGeomColorMode,
    collisionGeomColorsByKind,
    collisionGeomVisibility,
    equalityMarkerVisibility,
    focusBodyName: focusBodyUuid,
    focusCollisionGeomUuid,
    focusMeshUuid,
    focusModeActive,
    hoveredBody,
    hoveredJoint,
    hoveredMesh,
    inertialVisibility,
    isPhysicsRunning,
    jointEntries,
    jointKindsSignature,
    jointLookup,
    jointVizVisibility,
    lastSelectionSource,
    materials,
    meshFlatShading,
    meshOpacity,
    meshVisibility,
    safeJointMapObject,
    savedCameras,
    savedLights,
    selectedBodiesMultiKey,
    selectedBody,
    selectedEquality,
    selectedGeom,
    selectedInertial,
    selectedJoint,
    selectedMesh,
    selectedSite,
    selectedTendon,
    selectedVisualMeshIdsForScene,
    selectedVisualMeshMultiKey,
    selectionActions,
    showActuators,
    showCollision,
    showConstraintMarkers,
    showGrasps,
    showInertials,
    showJoints,
    showPositionGizmo,
    showSites,
    showVisual,
    siteVisibility,
    siteVizColor,
    siteVizSelectedColor,
    tendonMarkerVisibility,
    useYUp,
  } = useSceneViewportState();

  const { constraintMarkerLayerVisible, tendonMarkerLayerVisible } =
    useSceneConstraintVisualizationController({
      allEqualities,
      allTendons,
      cameraRef,
      constraintHoveredKeyRef,
      constraintMarkerBuildRetryCountRef,
      constraintMarkerMeshesRef,
      constraintMarkersGroupRef,
      constraintPointerRef,
      constraintVisibilityUi,
      constraintVisualizationRef,
      equalityMarkerVisibility,
      focusBodyUuid,
      focusCollisionGeomUuid,
      focusMeshUuid,
      focusModeActive,
      jointObjectsRef,
      missingConstraintBodyToastKeysRef,
      rendererRef,
      sceneObjectsRef,
      sceneRef,
      selectedBodiesMultiKey,
      selectedEquality,
      selectedTendon,
      showConstraintMarkers,
      structureChangeTick,
      tendonMarkerVisibility,
      tendonVisualizationRef,
    });
  const sceneActions = useSceneActions();
  const {
    findJointVizGroup,
    getJointIdFromObject,
    getJointSnapshot,
    jointPositionSliderOverlay,
    positionJointSliderNextToJoint,
    setJointPositionSliderScreenPos,
    updateJointVizPivotFromRef,
  } = useSceneJointRuntime({
    actuatorSliderState,
    cameraRef,
    isPhysicsRunning,
    jointLookup,
    rendererRef,
    safeJointMapObject,
    sceneActions,
    sceneRef,
    selectedJoint,
    showJointPositionSlider,
    showJoints,
  });
  const bodies = useBodies();
  const safeBodies = bodies ?? EMPTY_OBJECT;
  const worldBodies = useWorldBodies();

  const hfieldsForTerrainRepose = useSceneStore((s) => s.hfields);
  const liftBodiesWithTerrainEdits = React.useSyncExternalStore(
    subscribeTerrainVisualizationOptions,
    () => !!getTerrainVisualizationOptions().liftBodiesWithTerrainEdits,
    () => false
  );

  const {
    resolveSelectedBodyUuidForGizmo,
    resolveSelectedTargetForPositionGizmo,
    resolveSelectedTargetForRotationGizmo,
    resolveSelectedTargetForScaleGizmo,
    resolveSelectedVisualMeshGeomTargetsForGizmo,
  } = useGizmoTargetResolvers({ sceneRef });

  const { resolveLibraryDropPointFromClientPoint } = useLibraryDropPlacement({
    cameraRef,
    hfieldsForTerrainRepose,
    liftBodiesWithTerrainEdits,
    modelGroupRef,
    rendererRef,
    sceneObjectsRef,
    sceneRef,
    useYUp,
  });

  const {
    bodyTree,
    collectDescendantsByUuid,
    computeBodiesStructureSignature,
    computeFileMapSignature,
    effectiveWorldBodies,
    getRootUuids,
    getSortedFileKeys,
    hasWorldLevelSitesForRebuild,
    rebuildBodyTree,
    rebuildStructureSig,
  } = useSceneStructureSnapshot({ bodies, safeBodies, worldBodies });

  useSceneGraphCycleRepair({ sceneObjects, sceneRef });

  // Sync in-place material updates and assignments
  useMaterialSync({
    materials,
    sceneObjectsRef,
    sceneObjects,
  });

  // Repositioning logic removed - only XML-based robot loading is supported

  // Track when joint-driven kinematics are actively updating to avoid visual "jumps"
  const isJointUpdatingRef = useRef(false);

  useSceneBodyTransformSync({
    bodyTree,
    collectDescendantsByUuid,
    getJointSnapshot,
    isJointUpdatingRef,
    jointObjectsRef,
    safeBodies,
    sceneObjects,
  });

  // Compute effective highlighted states (hover takes precedence)
  const highlightedBody = hoveredBody || selectedBody;
  const highlightedMesh = hoveredMesh || selectedMesh;
  const highlightedJoint = hoveredJoint || selectedJoint;

  useThreeJsRenderer({
    mountRef,
    rendererRef,
    sceneRef,
    sceneObjectsRef,
    cameraRef,
    controlsRef,
    labelRendererRef,
    positionGizmoControllerRef,
    rotationGizmoControllerRef,
    scaleGizmoControllerRef,
    isOrbitingRef,
    preferPointerAfterOrbitRef,
    metricsActions,
    lastMetricsUpdate,
    jointLabelRefs,
    siteLabelsRef,
  });

  useSceneRebuild({
    bodyTree,
    rebuildBodyTree,
    hasWorldLevelSitesForRebuild,
    fileMap,
    getSortedFileKeys,
    getRootUuids,
    collectDescendantsByUuid,
    previousFileMapKeysRef,
    computeFileMapSignature,
    fileMapSignatureRef,
    rebuildStructureSig,
    computeBodiesStructureSignature,
    safeJointMapObject,
    jointKindsSignature,
    structureChangeTick,
    lastHandledStructureChangeTickRef,
    bodyTreeStructureSignatureRef,
    sceneRef,
    sceneObjectsRef,
    jointObjectsRef,
    siteLabelsRef,
    jointLabelRefs,
    modelGroupRef,
    sceneBuildTokenRef,
    removedBodyCacheRef,
    autoFitCameraRef,
    initialBuildDoneRef,
    previousBodiesRef,
    pendingMeshLoadBodyCountsRef,
    updatePendingMeshLoadBodyCount,
    registerPendingBodyMeshLoad,
    setSceneObjects,
    setJointMarkerSizes,
    applyJointMarkerSizes,
    jointMarkerScalePercent,
    jointMarkerSizes,
    jointMarkerAutoResizeRebuildGuardRef,
    isPhysicsRunning,
    getJointSnapshot,
    sceneActions,
    isHistoryRestoringRef,
    useYUp,
    showJoints,
    showCollision,
    showVisual,
    showSites,
    showInertials,
    materials,
    allSites,
    savedCameras,
    cameraMarkerVisibility,
    showSavedCameraMarkers,
    showViewportBodies,
    bodyVisibility,
    jointVizVisibility,
    meshVisibility,
    collisionGeomVisibility,
    inertialVisibility,
    selectedJoint,
    selectedSite,
    selectedBody,
    focusModeActive,
    selectionHighlightUi,
    bodyVisibilityUi,
    siteVisibilityUi,
    inertialVisibilityUi,
    jointVisibilityUi,
    structureChangeTickSetter: setStructureChangeTick,
  });

  useAutoFitCameraOnMeshLoad({ autoFitCameraRef, cameraRef, controlsRef, sceneRef });

  // Physics subscriptions and pose application
  usePhysicsPoses({
    modelGroupRef,
    sceneObjectsRef,
    jointObjectsRef,
  });

  // Mesh appearance and overlays
  useMeshAppearance({
    sceneRef,
    sceneObjects,
    meshOpacity,
    meshFlatShading,
    showVisual,
    showCollision,
    showSites,
    showJoints,
    showInertials,
    highlightedJoint,
    highlightedMesh,
    highlightedBody,
  });

  useCollisionGeomAppearance({
    sceneObjects,
    collisionGeomColor,
    collisionGeomColorMode,
    collisionGeomColorsByKind,
  });

  useSiteVizAppearance({
    sceneObjects,
    siteVizColor,
    siteVizSelectedColor,
    selectedSite,
  });

  useMeshEditorPreviewTransforms({ sceneRef });
  useMassPropertiesComMarkers({ sceneRef, structureChangeTick });
  useMeasurementOverlays({ sceneRef, structureChangeTick });

  useGraspOverlays({
    ikMarkerFocusGraspRef,
    sceneObjectsRef,
    sceneRef,
    selectedGraspByBodyRef,
    showGrasps,
  });

  useSceneVisibilityRules({
    allTendons,
    bodyVisibility,
    bodyVisibilityUi,
    cameraMarkerVisibility,
    collisionGeomVisibility,
    constraintMarkerLayerVisible,
    constraintVisibilityUi,
    effectiveWorldBodies,
    equalityMarkerVisibility,
    focusBodyUuid,
    focusCollisionGeomUuid,
    focusMeshUuid,
    focusModeActive,
    getJointIdFromObject,
    highlightedJoint,
    inertialVisibility,
    inertialVisibilityUi,
    inventoryLabelVisibility,
    jointVisibilityUi,
    jointVizVisibility,
    lastSelectionSource,
    meshVisibility,
    pendingMeshLoadBodyCountsRef,
    pendingMeshLoadBodyVersion,
    safeBodies,
    sceneObjects,
    sceneRef,
    selectedBodiesMultiKey,
    selectedBody,
    selectedEquality,
    selectedGeom,
    selectedInertial,
    selectedJoint,
    selectedSite,
    selectedTendon,
    showCollision,
    showActuators,
    showConstraintMarkers,
    showInertials,
    showJoints,
    showJointPositionSlider,
    showSavedCameraMarkers,
    showSites,
    showVisual,
    showViewportBodies,
    siteVisibility,
    siteVisibilityUi,
    tendonMarkerLayerVisible,
    tendonMarkerVisibility,
  });

  useActuatorMarkers({
    actuators,
    allTendons,
    jointObjectsRef,
    labelsVisible: inventoryLabelVisibility.actuator,
    safeJointMapObject,
    sceneObjects,
    sceneRef,
    showActuators,
    structureChangeTick,
  });

  useInventoryLabels({
    allMeshes,
    inventoryLabelVisibility,
    safeBodies,
    savedCameras,
    sceneObjects,
    sceneRef,
    structureChangeTick,
  });

  const {
    contextMenuProps,
    getOwningBodyUuidForObject,
    handleSceneContextMenuCapture,
    hoverPreviewBody,
    hoverPreviewJoint,
    hoverPreviewMesh,
    mobileViewportBlankTapHandlers,
    setHoverPreviewBody,
    setHoverPreviewJoint,
    setHoverPreviewMesh,
  } = useSceneInteractionController({
    allEqualities,
    allSites,
    allTendons,
    bodies,
    bodyVisibilityUi,
    cameraRef,
    constraintHoveredKeyRef,
    constraintMarkerMeshesRef,
    constraintMarkersGroupRef,
    constraintVisibilityUi,
    controlsRef,
    effectiveWorldBodies,
    focusBodyUuid,
    focusCollisionGeomUuid,
    focusMeshUuid,
    focusModeActive,
    getJointIdFromObject,
    inertialVisibilityUi,
    isOrbitingRef,
    isPhysicsRunning,
    jointVisibilityUi,
    positionJointSliderNextToJoint,
    preferPointerAfterOrbitRef,
    rendererRef,
    resolveLibraryDropPointFromClientPoint,
    safeBodies,
    safeJointMapObject,
    sceneActions,
    sceneObjectsRef,
    sceneRef,
    selectedBodiesMultiKey,
    selectedBody,
    selectedEquality,
    selectedGeom,
    selectedInertial,
    selectedJoint,
    selectedMesh,
    selectedSite,
    selectedTendon,
    selectedVisualMeshMultiKey,
    selectionActions,
    setJointPositionSliderScreenPos,
    showCollision,
    showConstraintMarkers,
    showInertials,
    showJointPositionSlider,
    showJoints,
    showSavedCameraMarkers,
    showScaleGizmo,
    showSites,
    showVisual,
    siteVisibilityUi,
    worldBodies,
  });

  useSelectionHighlightOverlays({
    bodies,
    highlightedBody,
    highlightedJoint,
    highlightedMesh,
    hoverPreviewBody,
    hoverPreviewJoint,
    hoverPreviewMesh,
    sceneObjects,
    sceneRef,
    selectedBodiesMultiKey,
    selectedGeom,
    selectedJoint,
    selectedVisualMeshIdsForScene,
    selectedVisualMeshMultiKey,
    selectionHighlightUi,
    showVisual,
  });

  useSceneSelectionOverlays({
    actuatedJointUuids,
    actuatorDisplayLabelByJointUuid,
    actuatorSignature,
    cameraRef,
    getJointIdFromObject,
    highlightedJoint,
    inertialVisibilityUi,
    jointEntries,
    jointVisibilityUi,
    positionJointSliderNextToJoint,
    rendererRef,
    safeJointMapObject,
    sceneObjects,
    sceneRef,
    selectedInertial,
    selectedJoint,
    selectedSite,
    selectionActions,
    setHoverPreviewBody,
    setHoverPreviewJoint,
    setHoverPreviewMesh,
    setJointPositionSliderScreenPos,
    showActuators,
    showJointPositionSlider,
    showJoints,
    siteVisibilityUi,
  });

  useFocusModeDim({
    focusBodyUuid,
    focusCollisionGeomUuid,
    focusMeshUuid,
    focusModeActive,
    getJointIdFromObject,
    getOwningBodyUuidForObject,
    sceneObjects,
    sceneRef,
    selectedBodiesMultiKey,
    selectedBody,
    selectedJoint,
    selectedMesh,
  });

  useJointVisualizationEvents({
    actuatorDisplayLabelByJointUuid,
    actuatorSignature,
    actuatedJointUuids,
    findJointVizGroup,
    getJointSnapshot,
    isPhysicsRunning,
    jointEntries,
    jointLookup,
    jointObjectsRef,
    jointVizVisibility,
    safeJointMapObject,
    sceneObjects,
    sceneObjectsRef,
    sceneRef,
    selectedJoint,
    showActuators,
    showJoints,
    slideJointLimitControlsRef,
    updateJointVizPivotFromRef,
  });

  useJointKinematicsEvents({
    findJointVizGroup,
    getJointSnapshot,
    isJointUpdatingRef,
    isPhysicsRunning,
    jointEntries,
    jointLookup,
    jointObjectsRef,
    sceneObjectsRef,
    sceneRef,
    slideJointLimitControlsRef,
    updateJointVizPivotFromRef,
  });

  useViewportBodyVisibilitySync({
    safeBodies,
    sceneObjectsRef,
    showViewportBodies,
    structureChangeTick,
  });

  useViewportLightingSync({ sceneRef });

  useSceneLightObjects({
    allSites,
    modelGroupRef,
    safeBodies,
    savedLights,
    sceneObjectsRef,
    sceneRef,
    structureChangeTick,
  });

  useSavedCameraMarkers({
    allSites,
    modelGroupRef,
    safeBodies,
    savedCameras,
    sceneObjectsRef,
    sceneRef,
    showSavedCameraMarkers,
    structureChangeTick,
  });

  useSavedLightMarkers({
    allSites,
    modelGroupRef,
    safeBodies,
    savedLights,
    sceneObjectsRef,
    sceneRef,
    structureChangeTick,
  });

  useIkTargetFollower();

  useGizmoControllers({
    showPositionGizmo,
    showRotationGizmo,
    showScaleGizmo,
    sceneRef,
    cameraRef,
    rendererRef,
    controlsRef,
    sceneObjectsRef,
    jointObjectsRef,
    positionGizmoControllerRef,
    rotationGizmoControllerRef,
    scaleGizmoControllerRef,
    isOrbitingRef,
    resolveSelectedBodyUuidForGizmo,
    resolveSelectedTargetForPositionGizmo,
    resolveSelectedTargetForRotationGizmo,
    resolveSelectedTargetForScaleGizmo,
    resolveSelectedVisualMeshGeomTargetsForGizmo,
  });

  useScenePhysicsLifecycle({ isPhysicsRunning, jointObjectsRef, sceneObjectsRef });

  // History sync
  useHistorySync({
    sceneRef,
    setSceneObjects,
    sceneObjectsRef,
    isHistoryRestoringRef,
  });

  useSceneDisposeOnUnmount({
    externalForceVizRef,
    sceneRef,
    modelGroupRef,
    sceneObjectsRef,
    zeroBodySyncWarningSignatureRef,
    rolloutProbeRef,
    physicsPoseLogDoneRef,
    padPoseLogDoneRef,
    physicsPoseApplyLoggedRef,
  });

  useSceneFloorSubscriptions({ sceneObjectsRef, sceneRef });

  // Reuse existing selectedJoint from selection store (declared earlier)

  useDebugPivotSpheres({
    findJointVizGroup,
    jointLookup,
    jointObjectsRef,
    sceneObjects,
    sceneRef,
    selectedJoint,
    showJoints,
  });

  const {
    handleTurnVisibilityOn: handleSelectedLayerVisibilityOnClick,
    notice: selectedLayerVisibilityNotice,
  } = useSelectedLayerVisibilityNotice({
    collisionGeomVisibility,
    equalityMarkerVisibility,
    inertialVisibility,
    jointVizVisibility,
    safeBodies,
    selectedEquality,
    selectedGeom,
    selectedInertial,
    selectedJoint,
    selectedSite,
    selectedTendon,
    showCollision,
    showConstraintMarkers,
    showInertials,
    showJoints,
    showSites,
    showVisual,
    siteVisibility,
    tendonMarkerVisibility,
  });
  return (
    <SceneViewportLayout
      actuatorSliderState={actuatorSliderState}
      contextMenuProps={contextMenuProps}
      handleSceneContextMenuCapture={handleSceneContextMenuCapture}
      handleSelectedLayerVisibilityOnClick={handleSelectedLayerVisibilityOnClick}
      jointPositionSliderOverlay={jointPositionSliderOverlay}
      mobileViewportBlankTapHandlers={mobileViewportBlankTapHandlers}
      selectedJoint={selectedJoint}
      selectedLayerVisibilityNotice={selectedLayerVisibilityNotice}
      setMountNodeRef={setMountNodeRef}
    />
  );
}

export default Scene;
